import * as React from 'react';
import {
  Button,
  Cover,
  Cluster,
  DateTime,
  Dialog,
  FormItem,
  Grid,
  Heading,
  Pagination,
  Select,
  Table,
  Template,
  TextInput,
} from '@pluto-tv/assemble';
import {debounce} from 'lodash-es';

import {ICampaignSlot} from 'models/campaigns';
import {IContent} from 'models/content';

import {useFindQuery} from 'features/content/contentApi';

export interface IPrimetimeBillboardSearchProps {
  isOpen: boolean;
  position: number;
  region?: string;
  campaignID: string;
  startDate?: Date;
  endDate?: Date;
  closeHandler: () => void;
  contentFoundHandler: (position: number, content: ICampaignSlot) => void;
}

export default ({
  isOpen,
  position,
  closeHandler,
  region = '',
  startDate,
  endDate,
  campaignID,
  contentFoundHandler,
}: IPrimetimeBillboardSearchProps): JSX.Element => {
  const [page, setPage] = React.useState(0);
  const [contentType, setContentType] = React.useState<'series' | 'movie' | 'channel' | 'vodcategory'>('series');
  const [search, setSearch] = React.useState('');
  const [rowsPerPage, setRowsPerPage] = React.useState<25 | 50 | 75 | 100>(25);
  const [selectedRow, setSelectedRow] = React.useState<IContent>();
  const [sortDir, setSortDir] = React.useState<'asc' | 'dsc'>('asc');

  const canBeOpen = isOpen && region.length > 0 && startDate && endDate ? true : false;

  const resetTable = (resetSearch = false, resetSort = false) => {
    if (resetSearch) {
      setSearch('');
    }

    if (resetSort) {
      setSortDir('asc');
    }

    setPage(0);
    setRowsPerPage(25);
    setSelectedRow(undefined);
  };

  const {data: content, isFetching} = useFindQuery(
    {
      campaignID,
      search,
      sort: `name:${sortDir}`,
      offset: page * rowsPerPage,
      limit: rowsPerPage,
      type: contentType,
    },
    {refetchOnMountOrArgChange: true},
  );

  React.useEffect(() => {
    resetTable(true, true);
  }, [contentType]);

  React.useEffect(() => {
    resetTable();
  }, [sortDir]);

  const searchTitle = debounce(
    searchStr => {
      setSearch(searchStr);
      resetTable(false, true);
    },
    500,
    {leading: false, trailing: true},
  );

  const selectContent = () => {
    if (!selectedRow) {
      return;
    }

    const newSlot: ICampaignSlot = {
      contentID: selectedRow._id,
      contentType: selectedRow.type,
      imagePreview: selectedRow.image,
      namePreview: selectedRow.name,
    };

    contentFoundHandler(position, newSlot);
  };

  return (
    <Dialog isOpen={canBeOpen} width='75%' height='75%' onClose={() => closeHandler()}>
      <Template label='header'>
        <Heading level='h3'>Select Content for Slot</Heading>
      </Template>
      <Template label='body'>
        <Cover gutterTop='small' scrolling={true}>
          <Template label='header'>
            <Cluster justify='center'>
              <Cluster buttonGroup={true}>
                <Button active={contentType === 'series'} onClick={() => setContentType('series')}>
                  Series
                </Button>
                <Button active={contentType === 'movie'} onClick={() => setContentType('movie')}>
                  Movies
                </Button>
                <Button active={contentType === 'channel'} onClick={() => setContentType('channel')}>
                  Channels
                </Button>
                <Button active={contentType === 'vodcategory'} onClick={() => setContentType('vodcategory')}>
                  VOD
                </Button>
              </Cluster>
            </Cluster>
          </Template>
          <Template label='cover'>
            <Cover scrolling={true} gutterBottom='medium'>
              <Template label='header'>
                <Grid gap='medium'>
                  <FormItem label='Filter'>
                    <TextInput
                      placeholder='Filter by Title'
                      icon='search'
                      value={search}
                      onChange={val => searchTitle(val)}
                    />
                  </FormItem>
                  <FormItem label='Region' state='disabled'>
                    <Select options={[{label: region}]} value={{label: region}} />
                  </FormItem>
                  <FormItem label='Publication Date' state='disabled'>
                    <DateTime
                      id='publicationDate'
                      range={true}
                      time={true}
                      appendToBody={true}
                      value={{start: new Date(startDate || ''), end: new Date(endDate || '')}}
                    />
                  </FormItem>
                </Grid>
              </Template>
              <Template label='cover'>
                <Table
                  fixedHeader={true}
                  selectable={true}
                  loading={isFetching}
                  emptyMsg='No results found'
                  onSelect={row => setSelectedRow(row as IContent)}
                  cols={[
                    {
                      label: 'ID',
                      field: '_id',
                    },
                    {
                      sortDir,
                      label: 'Title',
                      field: 'name',
                      sortable: true,
                    },
                    {
                      label: 'Region',
                      colWidth: '100px',
                      transform: row => row.region.toLocaleUpperCase(),
                    },
                    {
                      label: 'Start Availability',
                      hidden: contentType !== 'series' && contentType !== 'movie',
                      colWidth: '180px',
                      transform: row => new Date(row.availabilityStartDate).toLocaleString(),
                    },
                    {
                      label: 'End Availability',
                      hidden: contentType !== 'series' && contentType !== 'movie',
                      colWidth: '180px',
                      transform: row => new Date(row.availabilityEndDate).toLocaleString(),
                    },
                  ]}
                  rows={content?.data}
                  onSort={() => setSortDir(sortDir === 'asc' ? 'dsc' : 'asc')}
                ></Table>
              </Template>
              <Template label='footer'>
                <Cluster justify='space-between'>
                  <div></div>
                  <Pagination
                    perPage={rowsPerPage}
                    currentPage={page}
                    total={content?.metadata.totalCount || 0}
                    onPageChange={page => setPage(page)}
                    onPerPageChange={perPage => {
                      setRowsPerPage(perPage as 25 | 50 | 75 | 100);
                      setPage(0);
                    }}
                  />
                </Cluster>
              </Template>
            </Cover>
          </Template>
        </Cover>
      </Template>
      <Template label='footer'>
        <Cluster justify='space-between'>
          <div></div>
          <Cluster space='small'>
            <Button ghost={true} onClick={() => closeHandler()}>
              Cancel
            </Button>
            <Button type='primary' state={!selectedRow ? 'disabled' : ''} onClick={() => selectContent()}>
              Select
            </Button>
          </Cluster>
        </Cluster>
      </Template>
    </Dialog>
  );
};
